import DateFnsUtils from "@date-io/date-fns";
import {
  Breakpoint,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Input,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import moment from "moment";
import React, { useState } from "react";

interface UploadInputFieldProps {
  type?:
    | "text"
    | "boolean"
    | "select"
    | "date"
    | "dropdown"
    | "multidropdown"
    | "multiselect"
    | "date1";
  rows?: number;
  multiline?: boolean;
  maxRows?: number;
  labelWidth?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  inputWidth?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;
  label?: string;
  labelPadding?: boolean;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  hint?: string;
  options?: string[];
  displayValueForOptions?: string[];
  value?: string;
  checkedValues?: string[];
  selectAll?: boolean;
  disabledValues?: string[];
  onChange?: (value: string | null) => void;
  onSelectionChange?: (value: string[]) => void;
  shouldDisableDate?: (value: any) => boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  label: {
    display: "flex",
    [theme.breakpoints.only("xs")]: {
      paddingBottom: "0 !important",
    },
  },
  required: {
    color: "red",
    fontWeight: "bold",
  },
  input: {
    backgroundColor: "#fff",
    [theme.breakpoints.only("xs")]: {
      paddingTop: "0 !important",
    },
  },
  datePicker: {
    marginTop: "8px",
  },
}));

export default function DetailField(props: UploadInputFieldProps) {
  const width = useWidth();
  const classes = useStyles();
  const [value, setValue] = useState<string | null | undefined>(props.value);
  const [valueArray, setValueArray] = useState<string[]>(
    props.checkedValues || []
  );
  const [selectAllChecked, toggleSelectAll] = useState<boolean>(false);
  React.useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  React.useEffect(() => {
    const disabledLength =
      (props?.disabledValues && props.disabledValues.length) || 0;
    console.log(
      props.checkedValues && props.checkedValues.length,
      props?.options && props.options.length,
      disabledLength
    );
    if (
      props.checkedValues &&
      props.checkedValues.length ===
        (props?.options && props.options.length - disabledLength)
    ) {
      toggleSelectAll(true);
    }
  }, []);

  const handleChange = (val?: string | null) => {
    if (val !== undefined) {
      setValue(val);
    }
    if (props.onChange) {
      props.onChange(val || null);
    }
  };

  const handleArrayChange = (val?: string | null) => {
    console.log(props);
    if (val) {
      if (props.onSelectionChange) {
        // If the value exists in the array, remove it
        const valueIndex = valueArray.indexOf(val);
        let workingCopy;
        if (valueIndex > -1) {
          // Send a copy of the array without the provided value
          workingCopy = valueArray.filter((_, index) => index !== valueIndex);
        }
        // Otherwise add it
        else {
          workingCopy = valueArray.concat(val);
        }
        let uncheckSelectAll =
          (props?.options && props.options.length) === workingCopy.length;
        toggleSelectAll(uncheckSelectAll);
        props.onSelectionChange(workingCopy);
        setValueArray(workingCopy);
      } else if (props.onChange) {
        props.onChange(val);
        setValue(val);
      }
    }
  };

  const handleMultiDropDownControlChange = (
    event: SelectChangeEvent<string[]>
  ) => {
    setValueArray(event.target.value as string[]);
    if (props.onSelectionChange) {
      props.onSelectionChange(event.target.value as string[]);
    }
  };

  const handleSelectAllChange = () => {
    const isChecked = !selectAllChecked;
    toggleSelectAll(isChecked);
    const allOptions =
      props.disabledValues && props.disabledValues.length === 0
        ? Object.assign([], props.options)
        : Object.assign(
            [],
            props.options?.filter(
              (symptom) => props.disabledValues?.indexOf(symptom) === -1
            )
          );
    const workingCopy = isChecked ? allOptions : [];
    if (props.onSelectionChange) {
      props.onSelectionChange(workingCopy);
      setValueArray(workingCopy);
    }
  };
  // Restructured per https://codeburst.io/why-my-react-component-re-renders-a0d23614fab

  const BooleanControl = (
    <Grid item>
      <RadioGroup
        row
        onChange={(_, val) => handleChange(val)}
        value={value === undefined ? undefined : value === "true"}
      >
        <FormControlLabel
          disabled={props.disabled}
          value="true"
          control={<Radio />}
          label="Yes"
        />
        <FormControlLabel
          disabled={props.disabled}
          value="false"
          control={<Radio />}
          label="No"
        />
      </RadioGroup>
    </Grid>
  );

  const SelectControl = (
    <Grid item>
      <RadioGroup row onChange={(_, val) => handleChange(val)} value={value}>
        {props.options?.map((option, i) => (
          <FormControlLabel
            disabled={props.disabled}
            value={option}
            control={<Radio />}
            label={
              props.displayValueForOptions
                ? props.displayValueForOptions[i]
                : option
            }
          />
        ))}
      </RadioGroup>
    </Grid>
  );

  const MultiSelectControl = (
    <Grid container>
      <Grid item>
        {props.selectAll && (
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllChecked}
                onChange={handleSelectAllChange}
                name={"Select All"}
              />
            }
            label={"Select All"}
          />
        )}
      </Grid>
      <FormGroup row>
        {props.options?.map((option) => (
          <FormControlLabel
            control={
              <Checkbox
                disabled={
                  props.disabled ||
                  (props.disabledValues &&
                    props.disabledValues.indexOf(option) !== -1)
                }
                checked={valueArray.indexOf(option) > -1}
                onChange={() => handleArrayChange(option)}
                name={option}
              />
            }
            label={option}
          />
        ))}
      </FormGroup>
    </Grid>
  );

  const DropDownControl = (
    <Grid item>
      <Select
        disabled={props.disabled}
        onChange={(event) => handleChange(event.target.value as string)}
        value={value}
      >
        {props.options?.map((option, index) => (
          <MenuItem value={option}>
            {props.displayValueForOptions
              ? props.displayValueForOptions[index]
              : option}
          </MenuItem>
        ))}
      </Select>
    </Grid>
  );

  const MultiDropDownControl = (
    <Grid item>
      <Select
        disabled={props.disabled}
        multiple
        value={valueArray}
        onChange={handleMultiDropDownControlChange}
        input={<Input />}
        renderValue={(selected: any) => (selected as string[]).join(", ")}
      >
        {props.options?.map((o, i) => (
          <MenuItem key={o} value={o}>
            <Checkbox checked={valueArray.indexOf(o) > -1} />
            <ListItemText
              primary={
                props.displayValueForOptions
                  ? props.displayValueForOptions[i]
                  : o
              }
            />
          </MenuItem>
        ))}
      </Select>
    </Grid>
  );

  const TextControl = (
    <TextField
      size="small"
      disabled={props.disabled}
      fullWidth
      rows={props.rows}
      maxRows={props.maxRows}
      multiline={props.multiline}
      variant={props.readOnly ? "filled" : "outlined"}
      className={classes.input}
      inputProps={{
        readOnly: props.readOnly,
      }}
      value={value}
      onChange={(event) => handleChange(event.target.value)}
    />
  );

  const DateControl = (
    <LocalizationProvider dateAdapter={DateFnsUtils}>
      <DatePicker
        disableFuture
        label={props.labelWidth ? undefined : props.label}
        openTo="year"
        views={["month", "day"]}
        inputFormat="MM-dd-yyyy"
        disabled={props.disabled}
        value={value ? moment(value).toDate() : null}
        onChange={(val: any) =>
          handleChange(val ? moment(val).format("YYYY-MM-DD") : null)
        }
        renderInput={(params: any) => (
          <TextField {...params} fullWidth className={classes.datePicker} lab />
        )}
      />
    </LocalizationProvider>
  );

  const DateControlAlt1 = (
    <LocalizationProvider dateAdapter={DateFnsUtils}>
      <DatePicker
        disableFuture
        label="Responsive"
        openTo="year"
        views={["month", "day"]}
        inputFormat="MM/dd/yyyy"
        value={value ? moment(value, "MM/DD/YYYYY").toDate() : null}
        onChange={(val: any) =>
          handleChange(
            val ? moment(val, "MM/dd/yyyy").format("MM/DD/YYYYY") : null
          )
        }
        renderInput={(params: any) => (
          <TextField {...params} fullWidth className={classes.datePicker} />
        )}
      />
    </LocalizationProvider>
  );

  return (
    <Grid container spacing={3}>
      {props.labelWidth && (props.label || props.labelPadding) && (
        <Grid
          item
          xs={12}
          sm={12}
          md={props.labelWidth}
          justifyContent={
            width !== "xs" && width !== "sm" ? "center" : undefined
          }
          className={classes.label}
        >
          <Typography variant="h6">
            {props.label}{" "}
            <sup className={classes.required}>
              {props.required ? "*" : "\u00A0"}
            </sup>
          </Typography>
        </Grid>
      )}
      <Grid
        item
        xs={12}
        sm={12}
        md={
          ((props.inputWidth || 12) - (props.labelWidth || 0)) as
            | 1
            | 2
            | 3
            | 4
            | 5
            | 6
            | 7
            | 8
            | 9
            | 10
            | 11
            | 12
        }
      >
        {props.type === "boolean" && BooleanControl}
        {props.type === "select" && SelectControl}
        {props.type === "multiselect" && MultiSelectControl}
        {props.type === "dropdown" && DropDownControl}
        {props.type === "multidropdown" && MultiDropDownControl}
        {props.type === "text" && TextControl}
        {props.type === "date" && DateControl}
        {props.type === "date1" && DateControlAlt1}
      </Grid>
    </Grid>
  );
}

type BreakpointOrNull = Breakpoint | null;

function useWidth() {
  const theme: Theme = useTheme();
  const keys: readonly Breakpoint[] = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output: BreakpointOrNull, key: Breakpoint) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || "xs"
  );
}
